import turfSimplify from '@turf/simplify';
import * as crypto from 'crypto';
import { EventEmitter } from 'events';
import { Feature, FeatureCollection, GeoJsonTypes, Point, Polygon as GEOPolygon } from 'geojson';
import * as uniqid from 'uniqid';
import transform from "geojson-transformer";

interface IFrameNode {
  id: string;
  point: ILngLat;
  endpoint: boolean;
}

type Frame = IFrameNode[];

const md5 = (v: any) => crypto.createHash('md5').update(v).digest('hex');

const getMiddle = (prevPoint: ILngLat, nextPoint: ILngLat): ILngLat => {
  return [
    (prevPoint[0] + nextPoint[0]) / 2,
    (prevPoint[1] + nextPoint[1]) / 2,
  ];
};

const getHash = (obj: any, type: GeoJsonTypes, encode: 'wgs84' | 'gcj02') => {
  const str = JSON.stringify(obj);
  const md5Hash = md5(str);
  const suffix = Buffer.from(str).toString('base64').slice(-10);
  return `${type}.${encode}.${md5Hash}.${suffix}`;
};

export default class Polygon extends EventEmitter {
  public id = uniqid();

  private _frame: Frame = [];
  private _draggingNode?: IFrameNode;
  private _simplifyTolerance: number;

  public get hash() {
    return getHash(this.geometry.coordinates, 'Polygon', 'wgs84');
  }

  public get points() {
    return this._frame.filter(({ endpoint }) => endpoint).map(({ point }) => {
      return point as ILngLat;
    });
  }

  public get geometry(): GEOPolygon {
    const points = this.points;
    return {
      type: 'Polygon',
      coordinates: [[...points, points[0]]],
    };
  }

  public get geojsonEditor(): FeatureCollection<Point> {
    return {
      type: 'FeatureCollection',
      features: this._frame.map<Feature<Point>>(({ id, point, endpoint }) => {
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: point,
          },
          properties: {
            id,
            type: endpoint ? 0 : 1,
          },
        };
      }),
    };
  }

  public get geojsonPolygon(): Feature<GEOPolygon> {
    return {
      type: 'Feature',
      geometry: this.geometry,
      properties: {
        id: this.id,
      },
    };
  }

  constructor(points: ILngLat[], simplifyTolerance: number = 0) {
    super();
    this._simplifyTolerance = simplifyTolerance;
    this._initFrame(points);
    // this._initFrame(transform(points, 'gcj02', 'wgs84'));
  }

  public onMapDragStart(id: string) {
    const node = this._frame.find(frameNode => frameNode.id === id);
    if (node) {
      this._selectDraggingNode(node);
    }
  }

  public onMapDrag(lngLat: ILngLat) {
    if (this._draggingNode) {
      if (!this._draggingNode.endpoint) {
        this._addNode(this._draggingNode);
      }
      this._moveNode(lngLat);
    }
  }

  public onMapDragEnd() {
    this._finishMove();
  }

  public onMapRightClick(id: string) {
    if (this.points.length <= 3) {
      return;
    }
    const node = this._frame.find(frameNode => frameNode.id === id);
    if (node) {
      this._removeNode(node);
    }
  }

  private _addNode(frameNode: IFrameNode) {
    const index = this._frame.indexOf(frameNode);
    frameNode.endpoint = true;
    const prevPoint = this._frame[index - 1];
    const nextPoint = this._frame[index + 1 > this._frame.length - 1 ? 0 : index + 1];
    this._frame.splice(index + 1, 0, {
      id: uniqid(),
      endpoint: false,
      point: getMiddle(frameNode.point, nextPoint.point),
    });
    this._frame.splice(index, 0, {
      id: uniqid(),
      endpoint: false,
      point: getMiddle(prevPoint.point, frameNode.point),
    });
    this._triggerUpdated();
  }

  private _removeNode(frameEndpoint: IFrameNode) {
    const index = this._frame.indexOf(frameEndpoint);
    const prevNode = this._frame[index - 1 < 0 ? this._frame.length - 1 : index - 1];
    const nextNode = this._frame[index + 1];
    const prevPoint = this._frame[index - 2 < 0 ? this._frame.length - 2 : index - 2];
    const nextPoint = this._frame[index + 2 > this._frame.length - 1 ? 0 : index + 2];
    this._frame.splice(this._frame.indexOf(prevNode), 1);
    this._frame.splice(this._frame.indexOf(nextNode), 1);
    this._frame.splice(this._frame.indexOf(frameEndpoint), 1);
    this._frame.splice(this._frame.indexOf(prevPoint) + 1, 0, {
      id: uniqid(),
      endpoint: false,
      point: getMiddle(prevPoint.point, nextPoint.point),
    });
    this._triggerUpdated();
  }

  private _selectDraggingNode(frameEndpoint: IFrameNode) {
    this._draggingNode = frameEndpoint;
  }

  private _finishMove() {
    this._draggingNode = undefined;
    if (this._simplifyTolerance > 0) {
      this._initFrame(this.points);
    }
    this._triggerUpdated();
  }

  private _moveNode(lngLat: ILngLat) {
    if (!this._draggingNode) {
      return;
    }
    this._draggingNode.point = lngLat;
    const index = this._frame.indexOf(this._draggingNode);
    const prevNode = this._frame[index - 1 < 0 ? this._frame.length - 1 : index - 1];
    const nextNode = this._frame[index + 1];
    const prevPoint = this._frame[index - 2 < 0 ? this._frame.length - 2 : index - 2];
    const nextPoint = this._frame[index + 2 > this._frame.length - 1 ? 0 : index + 2];
    prevNode.point = getMiddle(prevPoint.point, lngLat);
    nextNode.point = getMiddle(lngLat, nextPoint.point);
    this._triggerUpdated();
  }

  private _initFrame(points: ILngLat[]) {
    if (this._simplifyTolerance > 0) {
      points = turfSimplify({
        type: 'Polygon',
        coordinates: [points],
      }, {
        tolerance: this._simplifyTolerance,
        mutate: true,
      }).coordinates[0];
    }
    const headPointStr = points[0].toString();
    const tailPointStr = points[points.length - 1].toString();
    if (headPointStr === tailPointStr) {
      points.pop();
    }
    const frame: Frame = [];
    points.reduce((prevPoint, nextPoint) => {
      frame.push({
        id: uniqid(),
        point: prevPoint,
        endpoint: true,
      }, {
        id: uniqid(),
        point: getMiddle(prevPoint, nextPoint),
        endpoint: false,
      });
      return nextPoint;
    }, points[points.length - 1]);
    this._frame = frame;
  }

  private _triggerUpdated() {
    this.emit('updated');
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1-0!./src/classes/loaders/Polygon.ts